import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { AlertTriangle, ArrowRight, BookOpen, Check as CheckIcon, Circle, ClipboardList, Clock, Download, Eye, FileText, Link2, Lock, Plus, Scale, Send, Wrench } from "lucide-react";
import { theme, card, btnPrimary, type as text } from "../../theme";
import { api, apiBlob, apiErrorMessage } from "../../api/client";
import { saveBlob, fileNameSafe } from "../../lib/download";
import { RegisterToolbar, SummaryTiles } from "../../components/RegisterToolbar";
import { downloadCsv, matchesSearch, by, type SortOption } from "../../lib/registerExport";
import { LoadingBlock } from "../../components/Spinner";
import { PortalLink } from "../../components/PortalLink";
import { MarkdownDocument } from "../../components/MarkdownDocument";
import { DetailFields, EditButton, RecordActions } from "../../components/RecordView";
import {
  ModalOverlay, ModalHeader, Field, Tri, MultiPick, EmptyState, input, textarea, secondaryBtn, dangerBtn,
  toLocalInput, fromInput, nowLocalInput, fmtDateTime, StatusPill,
} from "../resilience/shared";
import {
  classify, clockText, riskRef, byCriticality, span,
  Pill, VerdictPill, INCIDENT_STATUS, TONE, Section, ErrorText, useSaver, AlertList, GapsChecklist, CountBadge,
  CorrectiveActionList, RaiseCorrectiveAction, th, td, theadRow,
  type DoraContext, type DoraIncident, type Answers, type TriVal, type Clock as ClockT, type Criterion, type Tone,
} from "./shared";

export type IncidentFilter = "open" | "major" | "overdue" | "closed" | "all";
export const INCIDENT_FILTERS: IncidentFilter[] = ["open", "major", "overdue", "closed", "all"];

/** The next Art. 19 deadline not yet met, for sorting. */
function nextDue(i: DoraIncident): string | null {
  if (!i.clocks) return null;
  for (const c of [i.clocks.initial, i.clocks.intermediate, i.clocks.final]) if (c && !c.submittedAt) return c.dueAt;
  return null;
}
const iso = (v: string | null) => (v ? v.slice(0, 10) : "");

interface Props {
  canEdit: boolean;
  context: DoraContext | null;
  openId: string | null;
  onOpen: (id: string | null) => void;
  filter: IncidentFilter;
  onFilter: (f: IncidentFilter) => void;
  onChanged: () => void;
}

const STEP_LABEL: Record<"initial" | "intermediate" | "final", string> = {
  initial: "Initial notification",
  intermediate: "Intermediate report",
  final: "Final report",
};

function clockTone(c: ClockT | null): Tone {
  if (!c) return "neutral";
  if (c.state === "on_time") return "success";
  if (c.state === "late") return "warning";
  if (c.state === "overdue") return "danger";
  return new Date(c.dueAt).getTime() - Date.now() < 4 * 3_600_000 ? "warning" : "accent";
}

/** Art. 19 clocks in one line: initial → intermediate → final. */
export function ClockStrip({ incident: i }: { incident: DoraIncident }) {
  if (!i.clocks) {
    return <span style={{ ...text.hint, color: theme.color.faint }}>{i.classificationResult.verdict === "not_major" ? "Not reportable" : "—"}</span>;
  }
  const steps: Array<["initial" | "intermediate" | "final", ClockT | null]> = [["initial", i.clocks.initial], ["intermediate", i.clocks.intermediate], ["final", i.clocks.final]];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
      {steps.map(([k, c], idx) => {
        const t = TONE[clockTone(c)];
        return (
          <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {idx > 0 && <ArrowRight size={11} style={{ color: theme.color.faint }} />}
            <span title={c ? `${STEP_LABEL[k]}: due ${fmtDateTime(c.dueAt)}${c.submittedAt ? `, submitted ${fmtDateTime(c.submittedAt)}` : ""}` : `${STEP_LABEL[k]}: starts when the previous report is recorded`}
              style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 3, background: t.bg, color: t.color, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 3 }}>
              {c?.submittedAt ? <CheckIcon size={10} /> : c?.state === "overdue" ? <AlertTriangle size={10} /> : c ? <Clock size={10} /> : null}
              {k === "initial" ? "Initial" : k === "intermediate" ? "Interim" : "Final"}{c ? `: ${clockText(c)}` : ": waiting"}
            </span>
          </span>
        );
      })}
    </div>
  );
}

export function DoraIncidentsTab({ canEdit, context, openId, onOpen, filter, onFilter, onChanged }: Props) {
  const [incidents, setIncidents] = useState<DoraIncident[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("detected");

  const sortOptions: SortOption<DoraIncident>[] = useMemo(() => [
    { key: "detected", label: "Newest first", compare: by.date((i) => i.detectedAt, "desc") },
    { key: "due", label: "Next reporting deadline", compare: by.date(nextDue, "asc") },
    { key: "title", label: "Title", compare: by.text((i) => i.title) },
    { key: "gaps", label: "Most to do", compare: by.number((i) => i.gaps.length + i.alerts.length, "desc") },
    { key: "oldest", label: "Oldest first", compare: by.date((i) => i.detectedAt, "asc") },
  ], []);

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ incidents: DoraIncident[] }>("/dora/incidents");
      setIncidents(d.incidents);
      setError(null);
    } catch (e) { setError(apiErrorMessage(e, "Could not load the ICT incident register")); setIncidents([]); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const selected = useMemo(() => incidents?.find((i) => i.id === openId) ?? null, [incidents, openId]);

  function replace(updated: DoraIncident) {
    setIncidents((prev) => prev ? (prev.some((i) => i.id === updated.id) ? prev.map((i) => (i.id === updated.id ? updated : i)) : [updated, ...prev]) : [updated]);
    onChanged();
  }

  if (!incidents) return <LoadingBlock />;
  const open = incidents.filter((i) => i.status !== "CLOSED");
  const major = incidents.filter((i) => i.status !== "CLOSED" && i.classificationResult.verdict !== "not_major" && i.isMajor);
  const overdue = incidents.filter((i) => i.alerts.some((a) => a.severity === "breach"));
  const closed = incidents.filter((i) => i.status === "CLOSED");
  const byFilter = filter === "open" ? open : filter === "major" ? major : filter === "overdue" ? overdue : filter === "closed" ? closed : incidents;
  const shown = byFilter
    .filter((i) => matchesSearch(search, i.title, i.description, i.competentAuthority, i.supplier?.name, i.rootCause, i.geographicScope, ...i.assets.map((a) => a.asset.name)))
    .sort((sortOptions.find((o) => o.key === sort) ?? sortOptions[0]).compare);
  const toggle = (f: IncidentFilter) => onFilter(filter === f ? "all" : f);

  function exportCsv() {
    downloadCsv("dora-ict-incidents", [
      { header: "Incident", value: (i) => i.title },
      { header: "Detected", value: (i) => iso(i.detectedAt) },
      { header: "Classified", value: (i) => iso(i.classifiedAt) },
      { header: "Classification", value: (i) => i.classificationResult.verdict.replace("_", " ") },
      { header: "Status", value: (i) => i.status },
      { header: "Next step", value: (i) => context?.classification.stages[i.stage] ?? i.stage },
      { header: "Competent authority", value: (i) => i.competentAuthority },
      { header: "Initial notification", value: (i) => iso(i.initialNotifiedAt) },
      { header: "Initial reference", value: (i) => i.initialReference },
      { header: "Intermediate report", value: (i) => iso(i.intermediateReportAt) },
      { header: "Final report", value: (i) => iso(i.finalReportAt) },
      { header: "Clients affected", value: (i) => i.affectedClientsCount },
      { header: "Geographical scope", value: (i) => i.geographicScope },
      { header: "ICT provider", value: (i) => i.supplier?.name ?? "" },
      { header: "Systems affected", value: (i) => i.assets.map((a) => a.asset.name).join("; ") },
      { header: "Root cause", value: (i) => i.rootCause },
      { header: "Open deadline breaches", value: (i) => i.alerts.filter((a) => a.severity === "breach").length },
    ], shown);
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <SummaryTiles tiles={[
        { key: "open", label: "Open", value: open.length, tone: open.length ? "warning" : "neutral", active: filter === "open", onClick: () => toggle("open") },
        { key: "major", label: "Major or possibly major", value: major.length, tone: major.length ? "danger" : "neutral", active: filter === "major", onClick: () => toggle("major") },
        { key: "overdue", label: "Report overdue", value: overdue.length, hint: "an Art. 19 clock breached", tone: overdue.length ? "danger" : "success", active: filter === "overdue", onClick: () => toggle("overdue") },
        { key: "closed", label: "Closed", value: closed.length, tone: "neutral", active: filter === "closed", onClick: () => toggle("closed") },
        { key: "all", label: "All incidents", value: incidents.length, active: filter === "all", onClick: () => onFilter("all") },
      ]} />
      <RegisterToolbar<DoraIncident> search={search} onSearch={setSearch} placeholder="Search incidents, authority, provider, systems"
        sortOptions={sortOptions} sort={sort} onSort={setSort} onExport={exportCsv} count={{ shown: shown.length, total: incidents.length }}>
        {filter !== "all" && <button type="button" style={secondaryBtn} onClick={() => onFilter("all")}>Clear filter</button>}
        {canEdit && <button type="button" style={btnPrimary} onClick={() => setCreating(true)}><Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Record ICT incident</button>}
      </RegisterToolbar>
      <div style={{ ...text.hint, maxWidth: 820 }}>
        Record every ICT-related incident, then open it to classify it against the criteria in the classification RTS (Delegated Regulation 2024/1772). A major incident runs the Art. 19 reporting clocks: initial notification, intermediate report, final report. Open an incident to see its next step and what is missing.
      </div>
      {error && <div style={{ ...card, ...text.body, padding: 12, color: theme.color.dangerText }}>{error}</div>}

      {shown.length === 0 ? (
        <div style={card}>
          <EmptyState
            title={search ? "No incidents match your search" : filter === "all" ? "No ICT incidents recorded" : filter === "major" ? "No open major incidents" : filter === "overdue" ? "No report is overdue" : filter === "closed" ? "No closed incidents" : "No open ICT incidents"}
            detail="Record ICT-related incidents here, including ones you think are minor: classification is what decides whether one is reportable, and an unanswered question never counts as a no. Cyber incidents recorded under Operational resilience can be sent here with “Classify in DORA register”."
            action={canEdit ? <button type="button" style={btnPrimary} onClick={() => setCreating(true)}>Record an ICT incident</button> : undefined}
          />
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={theadRow}>
                {["Incident", "Detected", "Classification", "Art. 19 reporting", "Status", "To do"].map((h) => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {shown.map((i) => {
                const st = INCIDENT_STATUS[i.status] ?? { tone: "neutral" as Tone, label: i.status };
                const breaches = i.alerts.filter((a) => a.severity === "breach").length;
                const stageLabel = context?.classification.stages[i.stage] ?? i.stage;
                return (
                  <tr key={i.id} onClick={() => onOpen(i.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)" }}>
                    <td style={{ ...td, minWidth: 220 }}>
                      <div style={text.itemTitle}>{i.title}</div>
                      {i.stage !== "done" && <div style={{ ...text.hint, color: "var(--c-accentText)", marginTop: 3, fontWeight: 600 }}>Next step: {stageLabel}</div>}
                    </td>
                    <td style={{ ...td, color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDateTime(i.detectedAt)}</td>
                    <td style={td}><VerdictPill verdict={i.classificationResult.verdict} /></td>
                    <td style={td}><ClockStrip incident={i} /></td>
                    <td style={td}><Pill tone={st.tone}>{st.label}</Pill></td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                        <CountBadge n={breaches} tone="danger" title={`${breaches} deadline breach${breaches === 1 ? "" : "es"}`} />
                        <CountBadge n={i.gaps.length} tone="warning" title={i.gaps.join("\n")} />
                        {breaches === 0 && i.gaps.length === 0 && <span style={{ color: theme.color.successText, display: "inline-flex" }}><CheckIcon size={14} /></span>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && context && (
        <IncidentDetail key={selected.id} incident={selected} context={context} canEdit={canEdit} onClose={() => onOpen(null)} onUpdated={replace}
          onDeleted={async () => { onOpen(null); await load(); onChanged(); }} reload={load} />
      )}
      {creating && (
        <CreateIncident onClose={() => setCreating(false)} onCreated={(inc) => { replace(inc); setCreating(false); onOpen(inc.id); }} />
      )}
    </div>
  );
}

// ── Create ────────────────────────────────────────────────────────────────────

function CreateIncident({ onClose, onCreated }: { onClose: () => void; onCreated: (i: DoraIncident) => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [detectedAt, setDetectedAt] = useState(nowLocalInput());
  const saver = useSaver();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { saver.setError("Give the incident a title"); return; }
    await saver.run(async () => {
      const r = await api.post<{ incident: DoraIncident }>("/dora/incidents", {
        title: title.trim(), description, detectedAt: fromInput(detectedAt) ?? new Date().toISOString(), status: "OPEN",
      });
      onCreated(r.incident);
    }, "Could not record the incident");
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Record an ICT incident" onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gap: 12 }}>
        <Field label="Title"><input style={input} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoFocus placeholder="e.g. Payments gateway outage" /></Field>
        <Field label="What happened"><textarea style={textarea} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <Field label="Detected at" help="The 24-hour cap on the initial notification runs from this moment, whatever the classification.">
          <input type="datetime-local" style={input} value={detectedAt} onChange={(e) => setDetectedAt(e.target.value)} />
        </Field>
        <div style={{ ...text.hint, background: theme.color.panelAlt, borderRadius: 3, padding: "10px 12px" }}>
          Next, the incident opens so you can classify it, link the systems and supplier involved, and record reports to the authority.
        </div>
        <ErrorText error={saver.error} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="submit" style={btnPrimary} disabled={saver.busy}>{saver.busy ? "Saving…" : "Record and open"}</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Detail ────────────────────────────────────────────────────────────────────
//
// Opening an incident shows the record read-only. Each block carries its own Edit (for people who
// may edit), which swaps that block for its existing form; saving returns to the view. The state
// changes (record a report to the authority, close or reopen, raise a corrective action, delete)
// sit together under Actions at the foot.

type EditPart = "details" | "classification" | "authority" | "links" | "lessons";

function IncidentDetail({ incident: i, context, canEdit, onClose, onUpdated, onDeleted, reload }: {
  incident: DoraIncident; context: DoraContext; canEdit: boolean;
  onClose: () => void; onUpdated: (i: DoraIncident) => void; onDeleted: () => Promise<void>; reload: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<EditPart | null>(null);
  const put = async (body: Record<string, unknown>) => {
    const r = await api.put<{ incident: DoraIncident }>(`/dora/incidents/${i.id}`, body);
    onUpdated(r.incident);
  };
  const act = useSaver();
  const st = INCIDENT_STATUS[i.status] ?? { tone: "neutral" as Tone, label: i.status };
  const stageLabel = context.classification.stages[i.stage] ?? i.stage;
  const reported = !!i.initialNotifiedAt;
  const closed = i.status === "CLOSED";
  const done = () => setEditing(null);
  /** One block in its form at a time: the other blocks' Edit buttons wait until it is saved or cancelled. */
  const edit = (part: EditPart) => (canEdit && editing === null ? <EditButton onClick={() => setEditing(part)} /> : undefined);

  const closeMissing = [!i.rootCause?.trim() && "the root cause", !i.lessonsLearned?.trim() && "the lessons learned"].filter(Boolean) as string[];
  const finalOutstanding = i.isMajor && i.classificationResult.verdict !== "not_major" && !i.finalReportAt;

  async function remove() {
    if (!window.confirm(`Delete "${i.title}"? This cannot be undone.`)) return;
    await act.run(async () => { await api.del(`/dora/incidents/${i.id}`); await onDeleted(); }, "Could not delete the incident");
  }
  async function closeIncident() {
    if (finalOutstanding && !window.confirm("The final report has not been recorded. Closing does not stop its clock. Close anyway?")) return;
    await act.run(() => put({ rootCause: i.rootCause?.trim(), lessonsLearned: i.lessonsLearned?.trim(), status: "CLOSED" }), "Could not close the incident");
  }

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader
        title={<span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>{i.title} <VerdictPill verdict={i.classificationResult.verdict} /> <Pill tone={st.tone}>{st.label}</Pill></span>}
        onClose={onClose}
        right={canEdit && reported
          ? <span title="Reported to the authority: this is a regulatory record and must be kept. Close it instead." style={{ ...text.meta, display: "inline-flex", alignItems: "center", gap: 4 }}><Lock size={12} />Kept (reported)</span>
          : undefined}
      />
      <div style={{ padding: 20, display: "grid", gap: 18 }}>
        <AlertList alerts={i.alerts} />
        {i.stage !== "done" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 3, background: TONE.accent.bg, border: `1px solid ${TONE.accent.border}` }}>
            <ArrowRight size={16} style={{ color: TONE.accent.color, flexShrink: 0 }} />
            <div style={{ ...text.body, color: TONE.accent.color }}><strong>Next step:</strong> {stageLabel}</div>
          </div>
        )}
        <GapsChecklist gaps={i.gaps} />

        {editing === "details" ? <DetailsSection i={i} put={put} onDone={done} /> : (
          <Section title="Details" icon={<FileText size={15} />} right={edit("details")}>
            <DetailFields fields={[
              { label: "What happened", value: i.description, wide: true, multiline: true },
              { label: "Detected at", value: fmtDateTime(i.detectedAt) },
              { label: "Status", value: <Pill tone={st.tone}>{st.label}</Pill> },
              { label: "Clients affected", value: i.affectedClientsCount != null ? i.affectedClientsCount.toLocaleString("en-GB") : null },
              { label: "Geographical scope", value: i.geographicScope },
              { label: "Notes", value: i.notes, wide: true, multiline: true },
            ]} />
          </Section>
        )}

        <ClassificationSection key={editing === "classification" ? "edit" : "view"} i={i} criteria={context.classification.criteria}
          editing={editing === "classification"} right={edit("classification")} put={put} onDone={done} />

        <ReportingSection i={i} context={context} right={edit("authority")} canEdit={canEdit} onUpdated={onUpdated}
          authorityForm={editing === "authority" ? <AuthorityForm i={i} context={context} put={put} onDone={done} /> : null} />

        {editing === "links" ? <LinksSection i={i} context={context} put={put} onDone={done} /> : <LinksView i={i} right={edit("links")} />}

        {editing === "lessons" ? <LessonsForm i={i} put={put} onDone={done} /> : (
          <Section title="Root cause and lessons learned" icon={<BookOpen size={15} />} right={edit("lessons")}
            help="Both are needed before the incident can be closed (Art. 17(3) and the final report). A closed incident is written to the Evidence Repository against the DORA incident obligation.">
            <DetailFields fields={[
              { label: "Root cause", value: i.rootCause, wide: true, multiline: true },
              { label: "Lessons learned", value: i.lessonsLearned, wide: true, multiline: true },
            ]} />
          </Section>
        )}

        <Section title="Corrective actions" icon={<Wrench size={15} />} help="What will stop this happening again. Actions are tracked on the improvement programme and count as evidence of follow-up under Art. 17(3).">
          <CorrectiveActionList actions={i.correctiveActions} />
        </Section>

        {canEdit && (
          <RecordActions note={!closed && closeMissing.length > 0 ? `To close it, record ${closeMissing.join(" and ")} (Edit on Root cause and lessons learned).` : !closed && finalOutstanding ? "The final report is still outstanding." : undefined}>
            <RecordReportAction i={i} onUpdated={onUpdated} />
            {!closed ? (
              <button type="button" style={{ ...btnPrimary, opacity: closeMissing.length === 0 ? 1 : 0.5, cursor: closeMissing.length === 0 ? "pointer" : "not-allowed" }}
                disabled={act.busy || closeMissing.length > 0}
                title={closeMissing.length === 0 ? "Close the incident" : `Record ${closeMissing.join(" and ")} first`}
                onClick={closeIncident}>
                <ClipboardList size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Close incident
              </button>
            ) : (
              <button type="button" style={secondaryBtn} disabled={act.busy} onClick={() => act.run(() => put({ status: "OPEN" }), "Could not reopen the incident")}>Reopen</button>
            )}
            <RaiseCorrectiveAction
              source="incident" sourceRef={`dora-incident:${i.id}`}
              defaultTitle={`Remediate the cause of: ${i.title}`.slice(0, 300)} defaultPriority={i.isMajor ? "high" : "medium"}
              assetOptions={i.assets.map((a) => ({ id: a.asset.id, name: a.asset.name }))}
              onCreated={reload} />
            {!reported && <button type="button" style={dangerBtn} disabled={act.busy} onClick={remove}>Delete</button>}
          </RecordActions>
        )}
        <ErrorText error={act.error} />
      </div>
    </ModalOverlay>
  );
}

type Put = (body: Record<string, unknown>) => Promise<void>;

/** Save and Cancel for a block's form; Save returns to the view once it succeeds. */
function FormButtons({ busy, disabled, label, onSave, onCancel }: { busy: boolean; disabled?: boolean; label: string; onSave: () => void; onCancel: () => void }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
      <button type="button" style={btnPrimary} disabled={busy || disabled} onClick={onSave}>{busy ? "Saving…" : label}</button>
      <button type="button" style={secondaryBtn} disabled={busy} onClick={onCancel}>Cancel</button>
    </div>
  );
}

function DetailsSection({ i, put, onDone }: { i: DoraIncident; put: Put; onDone: () => void }) {
  const [f, setF] = useState({
    title: i.title, description: i.description, detectedAt: toLocalInput(i.detectedAt), awareAt: i.awareAt ? toLocalInput(i.awareAt) : "", status: i.status === "CLOSED" ? "CLOSED" : i.status,
    affectedClientsCount: i.affectedClientsCount?.toString() ?? "", geographicScope: i.geographicScope ?? "", notes: i.notes ?? "",
  });
  const saver = useSaver();
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));

  async function save() {
    if (!f.title.trim()) { saver.setError("The incident needs a title"); return; }
    const n = f.affectedClientsCount.trim();
    if (n && (!/^\d+$/.test(n))) { saver.setError("Clients affected must be a whole number"); return; }
    const ok = await saver.run(() => put({
      title: f.title.trim(), description: f.description, detectedAt: fromInput(f.detectedAt) ?? i.detectedAt, awareAt: f.awareAt ? fromInput(f.awareAt) : null,
      ...(f.status !== "CLOSED" && i.status !== "CLOSED" ? { status: f.status } : {}),
      affectedClientsCount: n ? Number(n) : null, geographicScope: f.geographicScope.trim() || null, notes: f.notes.trim() || null,
    }));
    if (ok) onDone();
  }

  return (
    <Section title="Edit details" icon={<FileText size={15} />}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <Field label="Title" span2><input style={input} value={f.title} onChange={(e) => set("title", e.target.value)} maxLength={200} /></Field>
        <Field label="What happened" span2><textarea style={textarea} rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <Field label="Detected at">
          <input type="datetime-local" style={input} value={f.detectedAt} onChange={(e) => set("detectedAt", e.target.value)} />
        </Field>
        <Field label="Became aware at" help="Anchors the 24-hour cap on the initial notification (RTS 2025/301 Art. 5(1)(a)). Leave empty when it is the detection time.">
          <input type="datetime-local" style={input} value={f.awareAt} onChange={(e) => set("awareAt", e.target.value)} />
        </Field>
        <Field label="Status" help={i.status === "CLOSED" ? "Closed. Reopen it from Actions." : "Close it from Actions, once the root cause and lessons learned are recorded."}>
          {i.status === "CLOSED" ? <input style={input} value="Closed" disabled /> : (
            <select style={input} value={f.status} onChange={(e) => set("status", e.target.value)}>
              <option value="OPEN">Open — still being handled</option>
              <option value="MONITORING">Monitoring — contained, watching for recurrence</option>
            </select>
          )}
        </Field>
        <Field label="Clients affected" help="Feeds the clients criterion (RTS Art. 1): over 10% or over 100,000.">
          <input style={input} inputMode="numeric" value={f.affectedClientsCount} onChange={(e) => set("affectedClientsCount", e.target.value)} />
        </Field>
        <Field label="Geographical scope" help="Member States with material impact (RTS Art. 4).">
          <input style={input} value={f.geographicScope} onChange={(e) => set("geographicScope", e.target.value)} maxLength={200} placeholder="e.g. Ireland, Luxembourg" />
        </Field>
        <Field label="Notes" span2><textarea style={textarea} rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
      </div>
      <FormButtons busy={saver.busy} label="Save details" onSave={save} onCancel={onDone} />
      <ErrorText error={saver.error} />
    </Section>
  );
}

const toTri = (v: TriVal | undefined): boolean | null => (v === "yes" ? true : v === "no" ? false : null);
const fromTri = (v: boolean | null): TriVal => (v === true ? "yes" : v === false ? "no" : "unknown");

/** The RTS criteria: answers shown as pills in the view, as yes / no / not sure toggles in the form. */
function ClassificationSection({ i, criteria, editing, right, put, onDone }: {
  i: DoraIncident; criteria: Criterion[]; editing: boolean; right?: React.ReactNode; put: Put; onDone: () => void;
}) {
  const [answers, setAnswers] = useState<Answers>(i.classification ?? {});
  const saver = useSaver();
  const shown = editing ? answers : (i.classification ?? {});
  const result = classify(shown, criteria);
  const dirty = criteria.some((c) => (answers[c.key] ?? "unknown") !== (i.classification?.[c.key] ?? "unknown"));
  const label = (k: string) => criteria.find((c) => c.key === k)?.label ?? k;
  const thresholdsMet = criteria.filter((c) => c.role === "threshold" && shown[c.key] === "yes").length;

  const groups: Array<{ role: Criterion["role"]; title: string; help: string }> = [
    { role: "gate", title: "1. The gate", help: "Every major incident affected critical or important services. If this is a no, the incident is not major." },
    { role: "route_a", title: "2. The direct route", help: "With the gate met, malicious unauthorised access on its own makes it major." },
    { role: "threshold", title: `3. Materiality thresholds — two are needed (${thresholdsMet} met)`, help: "With the gate met, any two of these make it major." },
    { role: "recurring", title: "4. Recurring incidents", help: "Minor occurrences with the same apparent root cause that together meet the thresholds." },
  ];

  const verdictTone: Tone = result.verdict === "major" ? "danger" : result.verdict === "not_major" ? "success" : "warning";
  const t = TONE[verdictTone];

  async function save() {
    const ok = await saver.run(() => put({ classification: answers }));
    if (ok) onDone();
  }

  return (
    <Section title={editing ? "Edit classification" : "Classification"} icon={<Scale size={15} />} right={editing ? undefined : right}
      help="Answer each criterion from the classification RTS. “Not sure” is never treated as a no: while anything that could make the incident major is unanswered, it stays possibly major and the 24-hour clock runs.">
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        <div style={{ display: "grid", gap: 14, flex: "3 1 340px", minWidth: 0 }}>
          {groups.map((g) => {
            const items = criteria.filter((c) => c.role === g.role);
            if (items.length === 0) return null;
            return (
              <div key={g.role}>
                <div style={{ ...text.label, color: theme.color.heading, marginBottom: 0 }}>{g.title}</div>
                <div style={{ ...text.meta, marginBottom: 6 }}>{g.help}</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {items.map((c) => {
                    const v = shown[c.key];
                    const missing = result.missing.includes(c.key);
                    return (
                      <div key={c.key} style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", padding: "8px 10px", borderRadius: 3, border: `1px solid ${missing ? theme.color.warning : "var(--c-border)"}`, background: v === "yes" ? theme.color.panelAlt : "transparent" }}>
                        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                          <div style={text.itemTitle}>{c.label} <span style={{ ...text.meta, fontWeight: 400 }}>{c.article}</span></div>
                          <div style={{ ...text.hint, marginTop: 2 }}>{c.help}</div>
                        </div>
                        {editing
                          ? <Tri value={toTri(v)} onChange={(nv) => setAnswers((p) => ({ ...p, [c.key]: fromTri(nv) }))} />
                          : <Pill tone={v === "yes" ? "danger" : v === "no" ? "success" : "neutral"}>{v === "yes" ? "Yes" : v === "no" ? "No" : "Not answered"}</Pill>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ position: "sticky", top: 60, display: "grid", gap: 10, flex: "2 1 240px", minWidth: 0 }}>
          <div style={{ padding: 14, borderRadius: 3, background: t.bg, border: `1px solid ${t.border}` }}>
            <div style={{ ...text.eyebrow, color: t.color }}>Verdict{editing && dirty ? " (unsaved)" : ""}</div>
            <div style={{ ...text.sectionTitle, color: t.color, margin: "4px 0 6px" }}>
              {result.verdict === "major" ? "Major incident" : result.verdict === "not_major" ? "Not major" : "Possibly major"}
            </div>
            <div style={{ ...text.hint, color: theme.color.text }}>{result.reasoning}</div>
            {result.missing.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ ...text.label, color: t.color, marginBottom: 4 }}>Still unanswered</div>
                <ul style={{ ...text.hint, color: theme.color.text, margin: 0, paddingLeft: 16 }}>
                  {result.missing.map((k) => <li key={k}>{label(k)}</li>)}
                </ul>
              </div>
            )}
            {editing && result.verdict === "major" && !i.isMajor && <div style={{ ...text.hint, marginTop: 8, color: t.color, fontWeight: 600 }}>Saving starts the 4-hour clock for the initial notification.</div>}
          </div>
          {editing && (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button type="button" style={btnPrimary} disabled={saver.busy || !dirty} onClick={save}>{saver.busy ? "Saving…" : "Save classification"}</button>
              <button type="button" style={secondaryBtn} disabled={saver.busy} onClick={onDone}>Cancel</button>
            </div>
          )}
          {i.classifiedAt && <div style={text.meta}>Classified {fmtDateTime(i.classifiedAt)}</div>}
          <ErrorText error={saver.error} />
        </div>
      </div>
    </Section>
  );
}

const AUTHORITY_KIND_LABELS: Record<string, string> = {
  financial_regulator: "Financial services regulator",
  essential_services: "Essential services / cyber authority",
  data_protection: "Data protection authority",
  cert: "National CERT / CSIRT",
  law_enforcement: "Law enforcement",
};

type ReportStage = "initial" | "intermediate" | "final";
interface StepInfo { stage: ReportStage; clock: ClockT | null; submittedAt: string | null; reference: string | null; rule: string }

function reportSteps(i: DoraIncident): StepInfo[] {
  return [
    { stage: "initial", clock: i.clocks?.initial ?? null, submittedAt: i.initialNotifiedAt, reference: i.initialReference, rule: "Within 4 hours of classifying it as major, and no later than 24 hours after becoming aware when classified within that time; classified later, within 4 hours of the classification (RTS 2025/301 Art. 5(1)(a), 5(2)). Nothing is due before classification." },
    { stage: "intermediate", clock: i.clocks?.intermediate ?? null, submittedAt: i.intermediateReportAt, reference: i.intermediateReference, rule: "Within 72 hours of the initial notification (sooner if regular activities are restored)." },
    { stage: "final", clock: i.clocks?.final ?? null, submittedAt: i.finalReportAt, reference: i.finalReference, rule: i.updatedIntermediateReportAt ? `Within one calendar month of the updated intermediate report of ${fmtDateTime(i.updatedIntermediateReportAt)} (RTS 2025/301 Art. 5(1)(c)).` : "Within one calendar month of the latest intermediate or updated intermediate report (RTS 2025/301 Art. 5(1)(c))." },
  ];
}

/** The competent authority form (Edit on the reporting block). */
function AuthorityForm({ i, context, put, onDone }: { i: DoraIncident; context: DoraContext; put: Put; onDone: () => void }) {
  const initialKey = i.authorityKey ?? (i.competentAuthority ? "__other" : "");
  const [key, setKey] = useState(initialKey);
  const [other, setOther] = useState(i.authorityKey ? "" : i.competentAuthority ?? "");
  const saver = useSaver();
  const chosen = context.authorities.find((a) => a.key === key);
  const kinds = Array.from(new Set(context.authorities.map((a) => a.kind)));
  const dirty = key !== initialKey || (key === "__other" && other.trim() !== (i.competentAuthority ?? ""));

  async function saveAuthority() {
    if (key === "__other" && !other.trim()) { saver.setError("Type the authority's name"); return; }
    const ok = await saver.run(() => put(key === "__other" ? { authorityKey: null, competentAuthority: other.trim() } : key ? { authorityKey: key } : { authorityKey: null, competentAuthority: null }));
    if (ok) onDone();
  }

  return (
    <div>
      <Field label="Competent authority" help={chosen ? `${chosen.country} — ${chosen.basis}` : key === "__other" ? "Not in your operating jurisdictions' catalogue: the name is stored as you type it." : "The authorities come from your operating locations; financial regulators are listed first."}>
        <select style={input} value={key} onChange={(e) => setKey(e.target.value)}>
          <option value="">Not chosen yet</option>
          {kinds.map((k) => (
            <optgroup key={k} label={AUTHORITY_KIND_LABELS[k] ?? k}>
              {context.authorities.filter((a) => a.kind === k).map((a) => <option key={a.key} value={a.key}>{a.name} ({a.country})</option>)}
            </optgroup>
          ))}
          <option value="__other">Another authority (type its name)</option>
        </select>
      </Field>
      {key === "__other" && (
        <div style={{ marginTop: 8 }}><input style={input} value={other} onChange={(e) => setOther(e.target.value)} maxLength={200} placeholder="Authority name" /></div>
      )}
      <FormButtons busy={saver.busy} disabled={!dirty} label="Save authority" onSave={saveAuthority} onCancel={onDone} />
      <ErrorText error={saver.error} />
    </div>
  );
}

function ReportingSection({ i, context, right, authorityForm, canEdit, onUpdated }: {
  i: DoraIncident; context: DoraContext; right?: React.ReactNode; authorityForm: React.ReactNode; canEdit: boolean; onUpdated: (i: DoraIncident) => void;
}) {
  const chosen = i.authorityKey ? context.authorities.find((a) => a.key === i.authorityKey) : undefined;
  const steps = reportSteps(i);
  const nextIdx = steps.findIndex((s) => !s.submittedAt);

  return (
    <Section title="Reporting to the authority (Art. 19)" icon={<Send size={15} />} right={authorityForm ? undefined : right}
      help="The clocks follow the reporting ITS: the initial notification is due 4 hours after classification, capped at 24 hours from detection; the intermediate report 72 hours after the initial notification; the final report one month after the intermediate report. Recording a submission (under Actions) stops its clock and starts the next.">
      <div style={{ display: "grid", gap: 16 }}>
        {authorityForm ?? (
          <DetailFields fields={[
            { label: "Competent authority", value: chosen ? `${chosen.name} (${chosen.country})` : i.competentAuthority },
            { label: "Basis", value: chosen ? `${chosen.country} — ${chosen.basis}` : null },
          ]} />
        )}

        {i.classificationResult.verdict === "not_major" ? (
          <div style={{ ...text.body, color: TONE.success.color, background: TONE.success.bg, borderRadius: 3, padding: "10px 12px" }}>
            Classified as not major: no Art. 19 reports are due. Record the root cause and close it when it is resolved.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 0 }}>
            {i.classificationResult.verdict === "undetermined" && (
              <div style={{ ...text.hint, color: TONE.warning.color, background: TONE.warning.bg, borderRadius: 3, padding: "8px 12px", marginBottom: 10 }}>
                Not yet classified, so it is treated as possibly major: the initial notification clock runs to the 24-hour cap from detection.
              </div>
            )}
            {steps.map((s, idx) => (
              <ReportStep key={s.stage} incidentId={i.id} incidentTitle={i.title} step={s} isNext={idx === nextIdx} isLast={idx === steps.length - 1} canEdit={canEdit} onUpdated={onUpdated} />
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

function ReportStep({ incidentId, incidentTitle, step, isNext, isLast, canEdit, onUpdated }: {
  incidentId: string; incidentTitle: string; step: StepInfo; isNext: boolean; isLast: boolean; canEdit: boolean; onUpdated: (i: DoraIncident) => void;
}) {
  const c = step.clock;
  const tone: Tone = step.submittedAt ? (c?.state === "late" ? "warning" : "success") : c ? clockTone(c) : "neutral";
  const t = TONE[tone];
  const icon = step.submittedAt ? <CheckIcon size={14} /> : c?.state === "overdue" ? <AlertTriangle size={14} /> : c ? <Clock size={14} /> : <Circle size={12} />;

  return (
    <div style={{ display: "flex", gap: 12 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ width: 28, height: 28, borderRadius: 2, background: t.bg, color: t.color, border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
        {!isLast && <div style={{ width: 2, flex: 1, minHeight: 16, background: "var(--c-border)" }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={text.itemTitle}>{STEP_LABEL[step.stage]}</span>
          {c && <Pill tone={tone}>{clockText(c)}</Pill>}
          {!c && <Pill tone="neutral">Waiting</Pill>}
          {isNext && c && <span style={{ ...text.meta, color: "var(--c-accentText)", fontWeight: 600 }}>Next to record</span>}
        </div>
        <div style={{ ...text.hint, marginTop: 3 }}>
          {step.submittedAt
            ? <>Submitted {fmtDateTime(step.submittedAt)}{c ? ` (due ${fmtDateTime(c.dueAt)})` : ""}. {step.reference ? <>Authority's reference: <strong style={{ color: theme.color.text }}>{step.reference}</strong></> : <span style={{ color: TONE.warning.color }}>No reference recorded.</span>}</>
            : c ? <>Due {fmtDateTime(c.dueAt)} — {new Date(c.dueAt).getTime() < Date.now() ? `${span(Date.now() - new Date(c.dueAt).getTime())} ago` : `in ${span(new Date(c.dueAt).getTime() - Date.now())}`}.</>
              : <>Starts when the previous report is recorded.</>}
        </div>
        <div style={{ ...text.meta, marginTop: 2 }}>{step.rule}</div>
        {canEdit && step.submittedAt && !step.reference && <AddReportReference incidentId={incidentId} step={step} onUpdated={onUpdated} />}
        <ReportDraftButtons incidentId={incidentId} incidentTitle={incidentTitle} stage={step.stage} />
      </div>
    </div>
  );
}

/**
 * A recorded report is never re-recorded (the server refuses it); the one thing that may follow it
 * is the authority's reference, when none was given at the time.
 */
function AddReportReference({ incidentId, step, onUpdated }: { incidentId: string; step: StepInfo; onUpdated: (i: DoraIncident) => void }) {
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState("");
  const saver = useSaver();
  if (!open) return <button type="button" style={{ ...secondaryBtn, marginTop: 6, padding: "3px 10px" }} onClick={() => setOpen(true)}>Add the authority's reference</button>;
  async function save() {
    if (!reference.trim()) { saver.setError("Enter the reference"); return; }
    const ok = await saver.run(async () => {
      const r = await api.post<{ incident: DoraIncident }>(`/dora/incidents/${incidentId}/reports`, { stage: step.stage, submittedAt: step.submittedAt, reference: reference.trim() });
      onUpdated(r.incident);
    }, "Could not record the reference");
    if (ok) setOpen(false);
  }
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
      <input style={{ ...input, maxWidth: 260 }} value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} placeholder="Acknowledgement or case number" />
      <button type="button" style={btnPrimary} disabled={saver.busy} onClick={save}>{saver.busy ? "Saving…" : "Save reference"}</button>
      <button type="button" style={secondaryBtn} disabled={saver.busy} onClick={() => setOpen(false)}>Cancel</button>
      <ErrorText error={saver.error} />
    </div>
  );
}

/** Actions: record the next Art. 19 submission (when it was sent and the authority's reference). */
function RecordReportAction({ i, onUpdated }: { i: DoraIncident; onUpdated: (i: DoraIncident) => void }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(nowLocalInput());
  const [reference, setReference] = useState("");
  const saver = useSaver();
  if (i.classificationResult.verdict === "not_major") return null;
  const next = reportSteps(i).find((s) => !s.submittedAt);
  if (!next || !next.clock) return null;
  const label = STEP_LABEL[next.stage];

  async function record() {
    const iso = fromInput(at);
    if (!iso) { saver.setError("Enter when it was submitted"); return; }
    const ok = await saver.run(async () => {
      const r = await api.post<{ incident: DoraIncident }>(`/dora/incidents/${i.id}/reports`, { stage: next!.stage, submittedAt: iso, reference: reference.trim() || null });
      onUpdated(r.incident);
    }, "Could not record the submission");
    if (ok) { setOpen(false); setReference(""); setAt(nowLocalInput()); }
  }

  if (!open) {
    return <button type="button" style={btnPrimary} onClick={() => setOpen(true)}><Send size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Record {label.toLowerCase()}</button>;
  }
  return (
    <div style={{ width: "100%", padding: 12, borderRadius: 3, border: "1px solid var(--c-border)", background: theme.color.panel, display: "grid", gap: 10, boxSizing: "border-box" }}>
      <div style={text.itemTitle}>Record {label.toLowerCase()}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
        <Field label="Submitted at"><input type="datetime-local" style={input} value={at} onChange={(e) => setAt(e.target.value)} /></Field>
        <Field label="Authority's reference" help="The acknowledgement or case number, if one was given."><input style={input} value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} /></Field>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" style={btnPrimary} disabled={saver.busy} onClick={record}>{saver.busy ? "Recording…" : `Record ${label.toLowerCase()}`}</button>
        <button type="button" style={secondaryBtn} disabled={saver.busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      <ErrorText error={saver.error} />
    </div>
  );
}

/** Linked records, read-only: systems, supplier, risk and the cyber incident record. */
function LinksView({ i, right }: { i: DoraIncident; right?: React.ReactNode }) {
  const critical = i.assets.filter((a) => a.asset.criticality === "critical" || a.asset.isEssentialFunction);
  return (
    <Section title="Linked records" icon={<Link2 size={15} />} right={right}
      help="The systems it hit, the supplier it came through and the risk it realised. The links put this incident on the asset, supplier and risk records, so nothing is looked at in isolation.">
      <div style={{ display: "grid", gap: 14 }}>
        <DetailFields fields={[
          {
            label: `Affected ICT systems (${i.assets.length})`, wide: true,
            value: i.assets.length === 0 ? null : (
              <span style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {[...i.assets.map((a) => a.asset)].sort(byCriticality).map((a) => (
                  <PortalLink key={a.id} to={`/resilience/assets?asset=${a.id}`} style={{ textDecoration: "none" }}>
                    <span style={{ ...text.hint, padding: "3px 10px", borderRadius: 2, display: "inline-block", border: "1px solid var(--c-border)", background: theme.color.panelAlt, color: theme.color.text }}>
                      {a.name} · {a.criticality}{a.isEssentialFunction ? " · essential" : ""}
                    </span>
                  </PortalLink>
                ))}
              </span>
            ),
          },
          { label: "Supplier it came through", value: i.supplier ? <PortalLink to={`/tprm?supplier=${i.supplier.id}`} style={{ color: "var(--c-accentText)" }}>{i.supplier.name}</PortalLink> : "None — in-house" },
          { label: "Risk it realised", value: i.linkedRisk ? <PortalLink to={`/risk-register?risk=${i.linkedRisk.id}`} style={{ color: "var(--c-accentText)" }}>{riskRef(i.linkedRisk)}</PortalLink> : null },
        ]} />
        {critical.length > 0 && <div style={text.hint}>{critical.length} of the linked systems support critical or important functions — that answers the gate (RTS Art. 6) in the classification.</div>}
        <div>
          <div style={{ ...text.label, marginBottom: 6 }}>Cyber incident record</div>
          {i.securityIncidents.length === 0 ? (
            <div style={text.hint}>Not linked to a cyber incident under Operational resilience. Other regulators, the insurer and data protection authorities are worked out there — use “Classify in DORA register” on the cyber incident to link the two.</div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {i.securityIncidents.map((s) => (
                <PortalLink key={s.id} to={`/resilience/incidents?incident=${s.id}`} style={{ textDecoration: "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 3, border: "1px solid var(--c-border)", background: theme.color.panelAlt }}>
                    <span style={{ ...text.ref, color: theme.color.muted }}>{s.displayId != null ? `INC-${String(s.displayId).padStart(3, "0")}` : "INC"}</span>
                    <span style={{ ...text.body, flex: 1 }}>{s.title}</span>
                    <StatusPill value={s.status} />
                  </div>
                </PortalLink>
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

function LinksSection({ i, context, put, onDone }: { i: DoraIncident; context: DoraContext; put: Put; onDone: () => void }) {
  const initialAssets = i.assets.map((a) => a.asset.id);
  const [assetIds, setAssetIds] = useState<string[]>(initialAssets);
  const [supplierId, setSupplierId] = useState(i.supplierId ?? i.supplier?.id ?? "");
  const [riskId, setRiskId] = useState(i.linkedRiskId ?? i.linkedRisk?.id ?? "");
  const saver = useSaver();
  const assets = [...context.assets].sort(byCriticality);
  const assetOptions = assets.map((a) => ({ id: a.id, name: `${a.name} · ${a.criticality}${a.isEssentialFunction ? " · essential" : ""}` }));
  const dirty = supplierId !== (i.supplierId ?? i.supplier?.id ?? "") || riskId !== (i.linkedRiskId ?? i.linkedRisk?.id ?? "")
    || assetIds.length !== initialAssets.length || assetIds.some((id) => !initialAssets.includes(id));
  const critical = i.assets.filter((a) => a.asset.criticality === "critical" || a.asset.isEssentialFunction);

  async function save() {
    const ok = await saver.run(() => put({ assetIds, supplierId: supplierId || null, linkedRiskId: riskId || null }));
    if (ok) onDone();
  }

  return (
    <Section title="Edit linked records" icon={<Link2 size={15} />}
      help="Say which systems it hit, the supplier it came through and the risk it realised.">
      <div style={{ display: "grid", gap: 14 }}>
        <Field label={`Affected ICT systems (${assetIds.length} selected)`} help={critical.length ? `${critical.length} of the linked systems support critical or important functions — that answers the gate (RTS Art. 6) in the classification.` : "Critical systems are listed first."}>
          <div style={{ maxHeight: 180, overflowY: "auto", padding: 2 }}><MultiPick options={assetOptions} selected={assetIds} onChange={setAssetIds} empty="No active assets in the asset register yet." /></div>
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          <Field label="Supplier it came through" help={context.suppliers.length ? undefined : "No suppliers in the supplier register."}>
            <select style={input} value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">None — in-house</option>
              {context.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.riskTier ? ` (${s.riskTier})` : ""}</option>)}
            </select>
          </Field>
          <Field label="Risk it realised">
            <select style={input} value={riskId} onChange={(e) => setRiskId(e.target.value)}>
              <option value="">None</option>
              {context.risks.map((r) => <option key={r.id} value={r.id}>{riskRef(r)}</option>)}
            </select>
          </Field>
        </div>
      </div>
      <FormButtons busy={saver.busy} disabled={!dirty} label="Save links" onSave={save} onCancel={onDone} />
      <ErrorText error={saver.error} />
    </Section>
  );
}

function LessonsForm({ i, put, onDone }: { i: DoraIncident; put: Put; onDone: () => void }) {
  const [rootCause, setRootCause] = useState(i.rootCause ?? "");
  const [lessons, setLessons] = useState(i.lessonsLearned ?? "");
  const saver = useSaver();
  const dirty = rootCause !== (i.rootCause ?? "") || lessons !== (i.lessonsLearned ?? "");

  async function save() {
    const ok = await saver.run(() => put({ rootCause: rootCause.trim() || null, lessonsLearned: lessons.trim() || null }));
    if (ok) onDone();
  }

  return (
    <Section title="Edit root cause and lessons learned" icon={<BookOpen size={15} />}
      help="Both are needed before the incident can be closed (Art. 17(3) and the final report). Close it from Actions once both are saved.">
      <div style={{ display: "grid", gap: 12 }}>
        <Field label="Root cause"><textarea style={textarea} rows={3} value={rootCause} onChange={(e) => setRootCause(e.target.value)} placeholder="What caused it, and why the controls did not stop it" /></Field>
        <Field label="Lessons learned"><textarea style={textarea} rows={3} value={lessons} onChange={(e) => setLessons(e.target.value)} placeholder="What changes as a result — raise each change as a corrective action" /></Field>
      </div>
      <FormButtons busy={saver.busy} disabled={!dirty} label="Save" onSave={save} onCancel={onDone} />
      <ErrorText error={saver.error} />
    </Section>
  );
}

// ── Report drafts (backend/src/lib/doraIncidentReport.ts) ─────────────────────

interface DraftResponse { stage: string; label: string; title: string; markdown: string; missing: string[] }

/** "Preview" and "Download draft" for one Art. 19 report: the register's content under the ITS field groups. */
function ReportDraftButtons({ incidentId, incidentTitle, stage }: { incidentId: string; incidentTitle: string; stage: "initial" | "intermediate" | "final" }) {
  const [draft, setDraft] = useState<DraftResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<"preview" | "download" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function preview() {
    if (open) { setOpen(false); return; }
    setBusy("preview"); setError(null);
    try { setDraft(await api.get<DraftResponse>(`/dora/incidents/${incidentId}/report-draft`, { stage })); setOpen(true); }
    catch (e) { setError(apiErrorMessage(e, "Could not prepare the draft")); }
    finally { setBusy(null); }
  }

  async function download() {
    setBusy("download"); setError(null);
    try {
      const blob = await apiBlob(`/dora/incidents/${incidentId}/report-draft.docx`, { stage });
      saveBlob(blob, `${fileNameSafe(`${STEP_LABEL[stage]} draft ${incidentTitle}`)}.docx`);
    } catch (e) { setError(apiErrorMessage(e, "Could not download the draft")); }
    finally { setBusy(null); }
  }

  const small: React.CSSProperties = { ...secondaryBtn, padding: "4px 10px", fontSize: text.hint.fontSize };
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button type="button" style={small} disabled={busy !== null} onClick={preview} aria-expanded={open}>
          <Eye size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{busy === "preview" ? "Preparing…" : open ? "Hide preview" : "Preview"}
        </button>
        <button type="button" style={small} disabled={busy !== null} onClick={download}>
          <Download size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{busy === "download" ? "Preparing…" : "Download draft"}
        </button>
      </div>
      {open && draft && (
        <div style={{ marginTop: 8, border: "1px solid var(--c-border)", borderRadius: 3, background: theme.color.panelAlt }}>
          {draft.missing.length > 0 && (
            <div style={{ ...text.hint, padding: "8px 12px", color: TONE.warning.color, borderBottom: "1px solid var(--c-border)" }}>
              {draft.missing.length} field{draft.missing.length === 1 ? "" : "s"} still to complete: {draft.missing.join(", ")}.
            </div>
          )}
          <div style={{ padding: 12, maxHeight: 360, overflow: "auto" }}><MarkdownDocument content={draft.markdown} size={12} /></div>
        </div>
      )}
      <ErrorText error={error} />
    </div>
  );
}
